import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControlLabel, IconButton, InputAdornment, List, ListItem, ListItemText, Stack, TextField, Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import tracker from '../../../api/tracker';
import useNotification from '../../../hooks/useNotificaction';
import { fDateTime } from '../../../utils/formatTime';
import PaymentQuotePreview from '../sacraments/PaymentQuotePreview';

export default function DocumentPaymentPanel({
  hashid,
  email,
  name,
  donationMade,
  receiptNumber,
  paymentDate,
  onPaidChange,
}) {
  const { t } = useTranslation();
  const [sendNotification] = useNotification();
  const [payment, setPayment] = useState(null);
  const [isLoadingPayment, setIsLoadingPayment] = useState(true);
  const [isGeneratingPayment, setIsGeneratingPayment] = useState(false);
  const [paymentConfirmOpen, setPaymentConfirmOpen] = useState(false);
  const [paymentDialogMode, setPaymentDialogMode] = useState('generate');
  const [paymentQuote, setPaymentQuote] = useState(null);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [refundConfirmOpen, setRefundConfirmOpen] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const [deductStripeFee, setDeductStripeFee] = useState(true);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [unmarkOpen, setUnmarkOpen] = useState(false);
  const [isSavingDonation, setIsSavingDonation] = useState(false);

  const paidOnline = payment?.status === 'paid' || payment?.status === 'partially_refunded';
  const isPaid = Boolean(donationMade) || paidOnline;
  const paidInOffice = isPaid && !paidOnline;

  const fetchPayment = async () => {
    setIsLoadingPayment(true);
    try {
      const res = await tracker.get(`docRequests/${hashid}/payment`);
      setPayment(res.data?.data ?? null);
    } catch {
      setPayment(null);
    } finally {
      setIsLoadingPayment(false);
    }
  };

  useEffect(() => {
    fetchPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashid]);

  const loadQuote = async () => {
    setIsLoadingQuote(true);
    setQuoteError(null);
    try {
      const res = await tracker.post('sacrament-payments/quote', {
        document_request_hashid: hashid,
      });
      setPaymentQuote(res.data);
    } catch (error) {
      setQuoteError(error?.response?.data?.message || t('error_loading_payment_amount'));
      setPaymentQuote(null);
    } finally {
      setIsLoadingQuote(false);
    }
  };

  const openPaymentDialog = (mode) => {
    setPaymentDialogMode(mode);
    setPaymentConfirmOpen(true);
    loadQuote();
  };

  const handleGeneratePayment = async () => {
    setIsGeneratingPayment(true);
    try {
      const res = await tracker.post('sacrament-payments', {
        document_request_hashid: hashid,
      });
      setPayment(res.data.data);
      setPaymentConfirmOpen(false);
      sendNotification({ msg: t('payment_link_generated'), variant: 'success' });
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || t('error_generating_payment_link'),
        variant: 'error',
      });
    } finally {
      setIsGeneratingPayment(false);
    }
  };

  const handleResendPayment = async () => {
    if (!payment?.token) return;
    setIsGeneratingPayment(true);
    try {
      const res = await tracker.post(`sacrament-payments/${payment.token}/resend`);
      setPayment(res.data.data);
      setPaymentConfirmOpen(false);
      sendNotification({ msg: t('payment_link_resent') || t('payment_link_generated'), variant: 'success' });
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || t('error_resending_payment_link') || t('error_generating_payment_link'),
        variant: 'error',
      });
    } finally {
      setIsGeneratingPayment(false);
    }
  };

  const handleCopyPaymentLink = () => {
    if (payment?.payment_url) {
      navigator.clipboard.writeText(payment.payment_url);
      sendNotification({ msg: t('copied_to_clipboard') || 'Enlace copiado', variant: 'success' });
    }
  };

  const saveDonation = async (made) => {
    setIsSavingDonation(true);
    try {
      await tracker.post(`docRequests/${hashid}/donation`, { donation_made: made });
      setMarkPaidOpen(false);
      setUnmarkOpen(false);
      sendNotification({
        msg: made ? t('donation_marked_paid') : t('donation_marked_unpaid'),
        variant: 'success',
      });
      if (onPaidChange) onPaidChange();
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || t('error_saving'),
        variant: 'error',
      });
    } finally {
      setIsSavingDonation(false);
    }
  };

  const estimateStripeFee = (gross, paymentData = payment) => {
    const amount = Number(gross || 0);
    if (!(amount > 0)) return 0;
    const pct = Number(paymentData?.stripe_fee_percent ?? 2.9);
    const fixed = Number(paymentData?.stripe_fee_fixed ?? 0.3);
    return Math.round((amount * (pct / 100) + fixed) * 100) / 100;
  };

  const getRefundBreakdown = (paymentData = payment) => {
    const total = Number(paymentData?.amount_total || 0);
    const refunded = Number(paymentData?.amount_refunded || 0);
    const refundable = Number(
      paymentData?.refundable_amount ?? Math.max(0, total - refunded)
    );
    const fee = Number(
      paymentData?.estimated_stripe_fee > 0
        ? paymentData.estimated_stripe_fee
        : estimateStripeFee(total, paymentData)
    );
    const net = Number(
      paymentData?.net_refundable_amount > 0
        ? paymentData.net_refundable_amount
        : Math.max(0, Math.round((total - fee - refunded) * 100) / 100)
    );
    return { total, refunded, refundable, fee, net };
  };

  const openRefundDialog = () => {
    setDeductStripeFee(true);
    setRefundReason('');
    setRefundConfirmOpen(true);
    const { refundable, net } = getRefundBreakdown();
    const suggested = net > 0 ? net : refundable;
    setRefundAmount(suggested > 0 ? suggested.toFixed(2) : '');
  };

  const handleRefundPayment = async () => {
    if (!payment?.token) return;
    setIsRefunding(true);
    try {
      const payload = { deduct_stripe_fee: deductStripeFee };
      const amount = Number(refundAmount);
      if (Number.isFinite(amount) && amount > 0) {
        payload.amount = amount;
      }
      if (refundReason.trim()) {
        payload.reason = refundReason.trim();
      }
      const res = await tracker.post(`sacrament-payments/${payment.token}/refund`, payload);
      setPayment(res.data.data);
      setRefundConfirmOpen(false);
      sendNotification({ msg: t('payment_refunded'), variant: 'success' });
      if (onPaidChange) onPaidChange();
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || t('error_refunding_payment'),
        variant: 'error',
      });
    } finally {
      setIsRefunding(false);
    }
  };

  const waitingForLink = payment?.status === 'pending' || payment?.status === 'expired';
  const canSendLink = !isPaid && (!payment || ['pending', 'expired', 'void'].includes(payment.status));
  const paidSourceText = paidOnline ? t('donation_received_online') : t('donation_received_office');
  const unpaidHint = waitingForLink ? t('donation_waiting_link') : t('donation_missing_hint');

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography variant="h6">{t('donation_status')}</Typography>
        <Chip
          icon={isPaid ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
          label={isPaid ? t('donation_paid') : t('donation_unpaid')}
          color={isPaid ? 'success' : 'warning'}
          sx={{ fontWeight: 700 }}
        />
      </Stack>

      {isLoadingPayment ? (
        <Typography color="text.secondary">{t('loading') || 'Cargando...'}</Typography>
      ) : (
        <Stack spacing={2}>
          <Alert severity={isPaid ? 'success' : 'warning'} variant="outlined">
            <Typography fontWeight={700} sx={{ mb: 0.5 }}>
              {isPaid ? t('donation_received_title') : t('donation_missing_title')}
            </Typography>
            <Typography variant="body2">
              {isPaid ? paidSourceText : unpaidHint}
            </Typography>
            {isPaid && (receiptNumber || paymentDate) && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {receiptNumber && <>{t('receipt_number')}: <strong>{receiptNumber}</strong></>}
                {receiptNumber && paymentDate ? ' · ' : ''}
                {paymentDate && fDateTime(paymentDate)}
              </Typography>
            )}
            {!isPaid && payment?.amount_total && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('amount_to_pay')}: <strong>${Number(payment.amount_total).toFixed(2)}</strong>
              </Typography>
            )}
          </Alert>

          {!isPaid && payment?.status === 'pending' && payment.payment_url && (
            <TextField
              fullWidth
              size="small"
              label={t('payment_link')}
              value={payment.payment_url}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopyPaymentLink} size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          {(payment?.last_sent_at || payment?.created_at) && !isPaid && (
            <Typography variant="body2" color="text.secondary">
              {t('last_payment_link_sent')}: {fDateTime(payment.last_sent_at || payment.created_at)}
              {payment.last_sent_by_name ? ` · ${payment.last_sent_by_name}` : ''}
            </Typography>
          )}

          {payment?.items?.length > 0 && isPaid && paidOnline && (
            <List dense>
              {payment.items.map((item, i) => (
                <ListItem key={i} disableGutters>
                  <ListItemText primary={item.description} secondary={`$${Number(item.amount).toFixed(2)}`} />
                </ListItem>
              ))}
            </List>
          )}

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {canSendLink && (
              <LoadingButton
                variant={isPaid ? 'outlined' : 'contained'}
                loading={isGeneratingPayment}
                onClick={() => openPaymentDialog(payment ? 'resend' : 'generate')}
                sx={{ background: isPaid ? undefined : '#032C65', '&:hover': { background: isPaid ? undefined : '#021a40' } }}
              >
                {payment ? t('resend_payment_link') : t('generate_payment_link')}
              </LoadingButton>
            )}

            {!isPaid && (
              <LoadingButton
                variant="outlined"
                color="success"
                loading={isSavingDonation}
                onClick={() => setMarkPaidOpen(true)}
              >
                {t('mark_donation_paid')}
              </LoadingButton>
            )}

            {paidInOffice && (
              <Button color="inherit" onClick={() => setUnmarkOpen(true)}>
                {t('mark_donation_unpaid')}
              </Button>
            )}

            {payment?.can_refund && (
              <LoadingButton variant="outlined" color="error" loading={isRefunding} onClick={openRefundDialog}>
                {t('refund_payment')}
              </LoadingButton>
            )}
          </Stack>
        </Stack>
      )}

      <Dialog
        open={paymentConfirmOpen}
        onClose={() => !isGeneratingPayment && setPaymentConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {paymentDialogMode === 'resend' ? t('resend_payment_link') : t('generate_payment_link')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t(paymentDialogMode === 'resend' ? 'resend_payment_link_confirm' : 'generate_payment_link_confirm')}
          </DialogContentText>
          <List dense>
            <ListItem disableGutters>
              <ListItemText primary={name} secondary={email || t('no_email')} />
            </ListItem>
          </List>
          <PaymentQuotePreview quote={paymentQuote} isLoading={isLoadingQuote} error={quoteError} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentConfirmOpen(false)} disabled={isGeneratingPayment}>
            {t('cancel_action')}
          </Button>
          <LoadingButton
            variant="contained"
            loading={isGeneratingPayment || isLoadingQuote}
            disabled={isLoadingQuote || !paymentQuote || Boolean(quoteError)}
            onClick={paymentDialogMode === 'resend' ? handleResendPayment : handleGeneratePayment}
            sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
          >
            {paymentDialogMode === 'resend' ? t('resend_payment_link') : t('generate_payment_link')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={markPaidOpen} onClose={() => !isSavingDonation && setMarkPaidOpen(false)}>
        <DialogTitle>{t('mark_donation_paid')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('mark_donation_paid_confirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidOpen(false)} disabled={isSavingDonation}>{t('cancel_action')}</Button>
          <LoadingButton color="success" variant="contained" loading={isSavingDonation} onClick={() => saveDonation(true)}>
            {t('mark_donation_paid')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={unmarkOpen} onClose={() => !isSavingDonation && setUnmarkOpen(false)}>
        <DialogTitle>{t('mark_donation_unpaid')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('mark_donation_unpaid_confirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnmarkOpen(false)} disabled={isSavingDonation}>{t('cancel_action')}</Button>
          <LoadingButton color="warning" variant="contained" loading={isSavingDonation} onClick={() => saveDonation(false)}>
            {t('mark_donation_unpaid')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={refundConfirmOpen}
        onClose={() => !isRefunding && setRefundConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('refund_payment')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t('refund_payment_confirm')}</DialogContentText>
          {(() => {
            const { refundable, fee, net } = getRefundBreakdown();
            return (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {t('refundable_amount')}: <strong>${refundable.toFixed(2)}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('estimated_stripe_fee')}: <strong>${fee.toFixed(2)}</strong>
                </Typography>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={deductStripeFee}
                      onChange={(e) => {
                        setDeductStripeFee(e.target.checked);
                        const next = e.target.checked && net > 0 ? net : refundable;
                        setRefundAmount(next > 0 ? next.toFixed(2) : '');
                      }}
                    />
                  )}
                  label={t('deduct_stripe_fee')}
                />
                <TextField
                  fullWidth
                  size="small"
                  label={t('refund_amount')}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  sx={{ mt: 2, mb: 2 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label={t('refund_reason')}
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                />
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundConfirmOpen(false)} disabled={isRefunding}>{t('cancel_action')}</Button>
          <LoadingButton color="error" variant="contained" loading={isRefunding} onClick={handleRefundPayment}>
            {t('confirm_refund')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

DocumentPaymentPanel.propTypes = {
  hashid: PropTypes.string.isRequired,
  email: PropTypes.string,
  name: PropTypes.string,
  donationMade: PropTypes.bool,
  receiptNumber: PropTypes.string,
  paymentDate: PropTypes.string,
  onPaidChange: PropTypes.func,
};

